import AiIcon from "../../Core/AiIcon/AiIcon";
import { Check, Loader2, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../ui/dialog";
import { ReportDashboard, ReportPlan } from "../../../models/ReportBuilder";
import { useGenerateWidget, usePlanReport } from "../../../networking/report-builder.hooks";
import { useReportBuilderStore } from "../../../hooks/reportBuilder.hooks";
import { resolveDateRange } from "./dateRange";
import { packPlacements } from "./rowLayout";

interface ReportPlanDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

type Step = {
	prompt: string;
	size: "S" | "M" | "L";
	status: "pending" | "running" | "done" | "failed";
	error?: string;
};

/**
 * "Bygg en dashboard om …": the backend plans name, period and widget
 * prompts; each widget is then generated one at a time so progress is
 * visible and one failure does not lose the rest.
 */
const ReportPlanDialog: React.FC<ReportPlanDialogProps> = ({ open, onOpenChange }) => {
	const store = useReportBuilderStore();
	const plan = usePlanReport();
	const generate = useGenerateWidget();
	const [prompt, setPrompt] = useState("");
	const [steps, setSteps] = useState<Step[]>([]);
	const [planned, setPlanned] = useState<ReportPlan>();
	const [building, setBuilding] = useState(false);
	const cancelled = useRef(false);

	useEffect(() => {
		if (open) {
			setPrompt("");
			setSteps([]);
			setPlanned(undefined);
			setBuilding(false);
			cancelled.current = false;
			plan.reset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const start = () => {
		const text = prompt.trim();
		if (!text) return;
		plan.mutate(
			{ prompt: text },
			{
				onSuccess: (p) => {
					setPlanned(p);
					setSteps(p.widgets.map((w) => ({ prompt: w.prompt, size: w.size, status: "pending" })));
				},
			}
		);
	};

	const build = async () => {
		if (!planned) return;
		setBuilding(true);
		const range = { preset: planned.period } as ReportDashboard["range"];
		const resolved = resolveDateRange(range);
		const dashboardId = store.createDashboard(planned.name, { range, compare: planned.compare });
		// S/M/L from the plan decide how many share a row; failed widgets simply leave a gap.
		const placements = packPlacements(steps.map((s) => s.size));
		for (let i = 0; i < steps.length; i++) {
			if (cancelled.current) break;
			setSteps((s) => s.map((x, j) => (j === i ? { ...x, status: "running" } : x)));
			try {
				const result = await generate.mutateAsync({
					prompt: steps[i].prompt,
					from: resolved.from,
					to: resolved.to,
					compare: planned.compare,
				});
				store.addWidget(dashboardId, { spec: result.spec, prompt: steps[i].prompt }, placements[i]);
				setSteps((s) => s.map((x, j) => (j === i ? { ...x, status: "done" } : x)));
			} catch (e: unknown) {
				const message =
					(e as { error?: { detail?: string; title?: string } })?.error?.detail ||
					(e as { error?: { title?: string } })?.error?.title ||
					"misslyckades";
				setSteps((s) =>
					s.map((x, j) => (j === i ? { ...x, status: "failed", error: message } : x))
				);
			}
		}
		setBuilding(false);
		store.setEditMode(true);
		if (!cancelled.current) onOpenChange(false);
	};

	const done = steps.filter((s) => s.status === "done").length;

	return (
		<Dialog
			open={open}
			onOpenChange={(o) => {
				if (!o) cancelled.current = true;
				onOpenChange(o);
			}}
		>
			<DialogContent className="z-[1300] w-[min(40rem,calc(100vw-2rem))] max-w-none">
				<DialogHeader>
					<DialogTitle>Ny dashboard från en beskrivning</DialogTitle>
					<DialogDescription>
						Beskriv vad dashboarden ska handla om. Du får ett förslag på widgets att godkänna innan
						de skapas.
					</DialogDescription>
				</DialogHeader>
				{!planned && (
					<>
						<textarea
							value={prompt}
							onChange={(e) => setPrompt(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter" && !e.shiftKey) {
									e.preventDefault();
									start();
								}
							}}
							rows={3}
							autoFocus
							placeholder="T.ex. kundanvändning i appen, eller lönsamhet per kund och konsult"
							className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-brand focus:outline-none"
						/>
						{plan.error && (
							<p className="text-xs text-red-600">
								{plan.error.error?.detail ||
									plan.error.error?.title ||
									"Kunde inte planera dashboarden"}
							</p>
						)}
					</>
				)}
				{planned && (
					<div className="space-y-2">
						<p className="text-sm">
							<span className="font-semibold">{planned.name}</span>
							<span className="text-gray-500"> · {planned.widgets.length} widgets</span>
						</p>
						<ul className="space-y-1">
							{steps.map((s, i) => (
								<li
									key={i}
									className="flex items-start gap-2 rounded-md border border-gray-200 px-2 py-1.5 text-sm"
								>
									<span className="mt-0.5 w-4 shrink-0 text-gray-400">
										{s.status === "running" && (
											<Loader2 className="h-4 w-4 animate-spin text-brand" />
										)}
										{s.status === "done" && <Check className="h-4 w-4 text-brand" />}
										{s.status === "failed" && <X className="h-4 w-4 text-danger" />}
									</span>
									<span className="min-w-0 flex-1">
										{s.prompt}
										<span className="ml-1 text-[10px] uppercase text-gray-400">{s.size}</span>
										{s.error && <span className="block text-xs text-danger">{s.error}</span>}
									</span>
									{!building && s.status === "pending" && (
										<button
											type="button"
											onClick={() => setSteps((all) => all.filter((_, j) => j !== i))}
											className="text-gray-400 hover:text-danger"
											aria-label="Ta bort"
										>
											<X className="h-4 w-4" />
										</button>
									)}
								</li>
							))}
						</ul>
					</div>
				)}
				<DialogFooter className="items-center gap-2">
					{building && (
						<span className="mr-auto text-xs text-gray-500">
							Skapar widget {Math.min(done + 1, steps.length)} av {steps.length}…
						</span>
					)}
					<button
						type="button"
						onClick={() => onOpenChange(false)}
						className="rounded-md border px-3 py-1.5 text-sm"
					>
						{building ? "Avbryt" : "Stäng"}
					</button>
					{!planned ? (
						<button
							type="button"
							onClick={start}
							disabled={plan.isLoading || !prompt.trim()}
							className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
						>
							{plan.isLoading ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<AiIcon className="h-4 w-4" />
							)}
							Föreslå widgets
						</button>
					) : (
						<button
							type="button"
							onClick={build}
							disabled={building || steps.length === 0}
							className="inline-flex items-center gap-2 rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
						>
							{building ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<AiIcon className="h-4 w-4" />
							)}
							Skapa dashboarden
						</button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default ReportPlanDialog;
